import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { getLoggedInUser, logout } from "../../helpers/authHelper";
import { AdminAccessProvider, AdminRouteGuard, moduleForPath, useAdminAccess } from "../AdminAccessContext";
import "./AdminLayout.css";
import "../../components/WorkspaceLayout.css";

const navigationSections = [
  {
    title: "Platform",
    items: [
      ["Overview", "/admin/dashboard", "ti-layout-dashboard"],
      ["Users", "/admin/users", "ti-users"],
      ["Support", "/admin/support-tickets", "ti-lifebuoy"],
      ["Plans & coupons", "/admin/subscriptions", "ti-ticket"],
    ],
  },
  {
    title: "Website",
    items: [
      ["Banners & carousel", "/admin/website/banners", "ti-speakerphone"],
      ["Contact submissions", "/admin/contact-submissions", "ti-mail-opened"],
    ],
  },
  {
    title: "Configuration",
    items: [
      ["Social integrations", "/admin/integrations", "ti-plug-connected"],
      ["AI providers", "/admin/ai-providers", "ti-sparkles"],
      ["Payment gateways", "/admin/payment-gateways", "ti-credit-card"],
      ["SMTP configuration", "/admin/smtp", "ti-mail-cog"],
    ],
  },
  {
    title: "Access",
    items: [
      ["Roles & permissions", "/admin/access-control", "ti-shield-lock"],
    ],
  },
];

const routeTitles = {
  "/admin/dashboard": "Platform overview",
  "/admin/users": "Users",
  "/admin/support-tickets": "Support tickets",
  "/admin/integrations": "Social integrations",
  "/admin/subscriptions": "Plans & coupons",
  "/admin/ai-providers": "AI provider settings",
  "/admin/payment-gateways": "Payment gateways",
  "/admin/smtp": "SMTP configuration",
  "/admin/access-control": "Roles & permissions",
  "/admin/website/banners": "Banners & carousel",
  "/admin/contact-submissions": "Contact submissions",
};

const AdminSidebar = ({ collapsed, onToggleCollapsed, onNavigate }) => {
  const navigate = useNavigate();
  const user = getLoggedInUser();
  const { access, canViewModule } = useAdminAccess();
  const visibleSections = navigationSections
    .map((section) => ({ ...section, items: section.items.filter(([, to]) => canViewModule(moduleForPath(to))) }))
    .filter((section) => section.items.length);
  const roleLabel = access ? access.isSuperAdmin ? "Super admin" : access.role?.name || "No role assigned" : "Administrator";
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "Administrator";
  const initials = displayName.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join("");

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <aside id="admin-sidebar" className="workspace-sidebar" aria-label="Administration navigation">
      <div className="workspace-sidebar-brand">
        <NavLink to="/admin/dashboard" onClick={onNavigate} className="workspace-brand-link" aria-label="Socialyorix admin home">
          <img className="workspace-brand-mark" src={`${process.env.PUBLIC_URL}/assets/images/logo/socialyorix-mark.png`} alt="" />
          <span className="workspace-brand-name"><img className="workspace-brand-logo" src={`${process.env.PUBLIC_URL}/assets/images/logo/socialyorix-logo.png`} alt="" /></span>
        </NavLink>
        <button
          className="workspace-collapse-button"
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <i className={`ti ${collapsed ? "ti-layout-sidebar-right-expand" : "ti-layout-sidebar-left-collapse"}`} aria-hidden="true" />
        </button>
      </div>

      <nav className="workspace-nav">
        {visibleSections.map((section) => (
          <div className="workspace-nav-section" key={section.title}>
            <h2 className="workspace-nav-heading">{section.title}</h2>
            {section.items.map(([label, to, icon]) => (
              <NavLink
                key={to}
                to={to}
                end
                onClick={onNavigate}
                className={({ isActive }) => `workspace-nav-link${isActive ? " is-active" : ""}`}
                aria-label={label}
                title={label}
                data-tooltip={label}
              >
                <i className={`ti ${icon}`} aria-hidden="true" />
                <span className="workspace-nav-label">{label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="workspace-sidebar-bottom">
        <div className="workspace-profile-link admin-profile">
          <span className="workspace-avatar" aria-hidden="true">{initials || "A"}</span>
          <span className="workspace-profile-copy"><strong>{displayName}</strong><small>{roleLabel}</small></span>
        </div>
        <button className="workspace-logout-link" type="button" onClick={handleLogout} aria-label="Logout" title="Logout" data-tooltip="Logout">
          <i className="ti ti-logout-2" aria-hidden="true" />
          <span className="workspace-nav-label">Sign out</span>
        </button>
      </div>
    </aside>
  );
};

const AdminLayout = () => <AdminAccessProvider><AdminLayoutFrame /></AdminAccessProvider>;

const AdminLayoutFrame = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("adminSidebarCollapsed") === "true");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 760px)").matches);

  useEffect(() => {
    const mobileQuery = window.matchMedia("(max-width: 760px)");
    const updateMobileMode = () => {
      setIsMobile(mobileQuery.matches);
      setMobileOpen(false);
    };
    mobileQuery.addEventListener("change", updateMobileMode);
    return () => mobileQuery.removeEventListener("change", updateMobileMode);
  }, []);

  useEffect(() => {
    localStorage.setItem("adminSidebarCollapsed", String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleExpiredSession = () => navigate("/login", { replace: true, state: { from: location } });
    window.addEventListener("auth:expired", handleExpiredSession);
    return () => window.removeEventListener("auth:expired", handleExpiredSession);
  }, [location, navigate]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [mobileOpen]);

  const sidebarCollapsed = collapsed && !isMobile;
  const currentTitle = routeTitles[location.pathname] || "Admin portal";

  return (
    <div data-bs-theme="light" className={`workspace-shell admin-shell${sidebarCollapsed ? " is-collapsed" : ""}${isMobile ? " is-mobile" : ""}${mobileOpen ? " mobile-open" : ""}`}>
      {mobileOpen && <button className="workspace-drawer-scrim" type="button" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />}
      <AdminSidebar
        collapsed={sidebarCollapsed}
        onToggleCollapsed={() => setCollapsed((value) => !value)}
        onNavigate={() => setMobileOpen(false)}
      />
      <div className="workspace-frame">
        <header className="workspace-topbar">
          <div className="workspace-topbar-start">
            <button className="workspace-menu-button" type="button" aria-label="Open navigation" aria-expanded={mobileOpen} aria-controls="admin-sidebar" onClick={() => setMobileOpen(true)}>
              <i className="ti ti-menu-2" aria-hidden="true" />
            </button>
            <div className="workspace-breadcrumb"><span>Admin portal</span><i className="ti ti-chevron-right" aria-hidden="true" /><strong>{currentTitle}</strong></div>
          </div>
          <div className="admin-topbar-status"><span className="workspace-status-dot" /> ADMIN PORTAL</div>
        </header>
        <main key={location.pathname} className="workspace-content workspace-page"><AdminRouteGuard><Outlet /></AdminRouteGuard></main>
      </div>
    </div>
  );
};

export default AdminLayout;
